import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Menu,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TablePagination,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  ViewColumn as ColumnsIcon,
  Search as SearchIcon,
  CompareArrows,
  Inventory,
  Assessment,
  Speed,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { BranchTransfer } from '../../types/branchTransfers.types';
import { branchTransfersService } from '../../services/branchTransfers.service';
import { Branch } from '../../types/branches.types';
import { branchesService } from '../../services/branches.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { fmtUnitPrice } from '../../utils/formatNumber';

const translations = {
  en: {
    title: 'Branch Transfers Report',
    titleAr: 'تقرير نقل المخزون بين الفروع',
    fromBranch: 'From Branch',
    toBranch: 'To Branch',
    allBranches: 'All Branches',
    allStatuses: 'All Statuses',
    status: 'Status',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    search: 'Search...',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisYear: 'This Year',
    detailedView: 'Detailed Transfers',
    summaryByFromBranch: 'Summary by Source',
    summaryByToBranch: 'Summary by Destination',
    summaryByItem: 'Summary by Item',
    transferNumber: 'Transfer #',
    transferDate: 'Date',
    fromBranchName: 'From Branch',
    toBranchName: 'To Branch',
    itemCode: 'Item Code',
    itemName: 'Item',
    unitName: 'Unit',
    quantity: 'Quantity',
    unitCost: 'Unit Cost',
    totalCost: 'Total',
    notes: 'Notes',
    totalTransfers: 'Total Transfers',
    totalQuantity: 'Total Qty',
    totalValue: 'Total Value',
    avgTransferAmount: 'Avg Amount',
    draft: 'Draft',
    approved: 'Approved',
    stats: 'Statistics',
    transfersCount: 'Total Transfers',
    itemsTransferred: 'Items Transferred',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    columns: 'Columns',
    filters: 'Filters',
    noData: 'No branch transfers found',
    loading: 'Loading...',
    kwd: 'KWD',
  },
  ar: {
    title: 'تقرير نقل المخزون بين الفروع',
    titleAr: 'تقرير نقل المخزون بين الفروع',
    fromBranch: 'من فرع',
    toBranch: 'إلى فرع',
    allBranches: 'جميع الفروع',
    allStatuses: 'جميع الحالات',
    status: 'الحالة',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    search: 'بحث...',
    today: 'اليوم',
    thisWeek: 'هذا الأسبوع',
    thisMonth: 'هذا الشهر',
    lastMonth: 'الشهر الماضي',
    thisYear: 'هذه السنة',
    detailedView: 'التحويلات التفصيلية',
    summaryByFromBranch: 'ملخص حسب الفرع المصدر',
    summaryByToBranch: 'ملخص حسب الفرع الوجهة',
    summaryByItem: 'ملخص حسب الصنف',
    transferNumber: 'رقم التحويل',
    transferDate: 'التاريخ',
    fromBranchName: 'من فرع',
    toBranchName: 'إلى فرع',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    unitName: 'الوحدة',
    quantity: 'الكمية',
    unitCost: 'سعر الوحدة',
    totalCost: 'الإجمالي',
    notes: 'ملاحظات',
    totalTransfers: 'إجمالي التحويلات',
    totalQuantity: 'إجمالي الكمية',
    totalValue: 'القيمة الإجمالية',
    avgTransferAmount: 'متوسط المبلغ',
    draft: 'مسودة',
    approved: 'معتمد',
    stats: 'الإحصائيات',
    transfersCount: 'عدد التحويلات',
    itemsTransferred: 'الأصناف المحولة',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    columns: 'الأعمدة',
    filters: 'الفلاتر',
    noData: 'لا توجد تحويلات فروع',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
  },
};

interface DetailedTransfer {
  transferNumber: string;
  transferDate: string;
  fromBranchId: number;
  fromBranchName: string;
  toBranchId: number;
  toBranchName: string;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  unitName: string;
  unitNameAr: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  status: string;
}

interface BranchSummary {
  branchId: number;
  branchName: string;
  branchNameAr: string;
  transfersCount: number;
  totalQuantity: number;
  totalValue: number;
}

interface ItemSummary {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  transfersCount: number;
  totalQuantity: number;
  avgCost: number;
  totalValue: number;
}

const BranchTransfersReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  const [transfers, setTransfers] = useState<BranchTransfer[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [fromBranchFilter, setFromBranchFilter] = useState<number | ''>('');
  const [toBranchFilter, setToBranchFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [datePreset, setDatePreset] = useState<string>('');
  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(25);

  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState({
    transferNumber: true,
    date: true,
    fromBranch: true,
    toBranch: true,
    item: true,
    quantity: true,
    cost: true,
    total: true,
  });

  useEffect(() => {
    loadBranches();
    loadCompanyInfo();
  }, []);

  useEffect(() => {
    loadData();
    setDetailPage(0);
  }, [fromBranchFilter, toBranchFilter, statusFilter, dateFromFilter, dateToFilter]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();
      setBranches(data);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await branchTransfersService.getAll(
        statusFilter || undefined,
        fromBranchFilter || undefined,
        toBranchFilter || undefined,
        dateFromFilter || undefined,
        dateToFilter || undefined
      );
      setTransfers(data);
    } catch (error) {
      console.error('Failed to load branch transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const today = new Date();
    let fromDate = '';
    let toDate = today.toISOString().split('T')[0];

    switch(preset) {
      case 'today':
        fromDate = toDate;
        break;
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        fromDate = weekStart.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        fromDate = lastMonth.toISOString().split('T')[0];
        toDate = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case 'thisYear':
        fromDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
    }

    setDateFromFilter(fromDate);
    setDateToFilter(toDate);
  };

  const getDetailedData = (): DetailedTransfer[] => {
    const detailed: DetailedTransfer[] = [];
    
    transfers.forEach(transfer => {
      transfer.details.forEach(detail => {
        detailed.push({
          transferNumber: transfer.transferNumber,
          transferDate: transfer.transferDate,
          fromBranchId: transfer.fromBranchId,
          fromBranchName: isRTL ? (transfer.fromBranchNameAr || transfer.fromBranchName || '-') : (transfer.fromBranchName || '-'),
          toBranchId: transfer.toBranchId,
          toBranchName: isRTL ? (transfer.toBranchNameAr || transfer.toBranchName || '-') : (transfer.toBranchName || '-'),
          itemCode: detail.itemCode,
          itemName: detail.itemName,
          itemNameAr: detail.itemNameAr,
          unitName: detail.unitName,
          unitNameAr: detail.unitNameAr,
          quantity: detail.quantity,
          unitCost: detail.unitCost,
          totalCost: detail.unitCost * detail.quantity,
          status: transfer.status || 'Draft',
        });
      });
    });

    return detailed;
  };

  const getFilteredData = () => {
    let filtered = getDetailedData();
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.transferNumber.toLowerCase().includes(query) ||
        r.fromBranchName.toLowerCase().includes(query) ||
        r.toBranchName.toLowerCase().includes(query) ||
        r.itemName.toLowerCase().includes(query) ||
        r.itemCode.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const getSummaryByFromBranch = (): BranchSummary[] => {
    const summary = new Map<number, BranchSummary>();
    
    transfers.forEach(transfer => {
      if (!summary.has(transfer.fromBranchId)) {
        summary.set(transfer.fromBranchId, {
          branchId: transfer.fromBranchId,
          branchName: transfer.fromBranchName || '-',
          branchNameAr: transfer.fromBranchNameAr || '-',
          transfersCount: 0,
          totalQuantity: 0,
          totalValue: 0,
        });
      }
      
      const br = summary.get(transfer.fromBranchId)!;
      br.transfersCount++;
      transfer.details.forEach(d => {
        br.totalQuantity += d.quantity;
        br.totalValue += d.unitCost * d.quantity;
      });
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalValue - a.totalValue);
  };

  const getSummaryByToBranch = (): BranchSummary[] => {
    const summary = new Map<number, BranchSummary>();
    
    transfers.forEach(transfer => {
      if (!summary.has(transfer.toBranchId)) {
        summary.set(transfer.toBranchId, {
          branchId: transfer.toBranchId,
          branchName: transfer.toBranchName || '-',
          branchNameAr: transfer.toBranchNameAr || '-',
          transfersCount: 0,
          totalQuantity: 0,
          totalValue: 0,
        });
      }
      
      const br = summary.get(transfer.toBranchId)!;
      br.transfersCount++;
      transfer.details.forEach(d => {
        br.totalQuantity += d.quantity;
        br.totalValue += d.unitCost * d.quantity;
      });
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalValue - a.totalValue);
  };

  const getSummaryByItem = (): ItemSummary[] => {
    const summary = new Map<number, ItemSummary>();
    
    transfers.forEach(transfer => {
      transfer.details.forEach(detail => {
        if (!summary.has(detail.itemId)) {
          summary.set(detail.itemId, {
            itemId: detail.itemId,
            itemCode: detail.itemCode,
            itemName: detail.itemName,
            itemNameAr: detail.itemNameAr,
            transfersCount: 0,
            totalQuantity: 0,
            avgCost: 0,
            totalValue: 0,
          });
        }
        
        const item = summary.get(detail.itemId)!;
        item.transfersCount++;
        item.totalQuantity += detail.quantity;
        item.totalValue += detail.unitCost * detail.quantity;
      });
    });
    
    summary.forEach(item => {
      item.avgCost = item.totalQuantity > 0 ? item.totalValue / item.totalQuantity : 0;
    });
    
    return Array.from(summary.values()).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const getStatistics = () => {
    const detailed = getDetailedData();
    const uniqueItems = new Set(detailed.map(d => d.itemCode)).size;
    const totalValue = detailed.reduce((sum, d) => sum + d.totalCost, 0);
    
    return {
      transfersCount: transfers.length,
      itemsTransferred: uniqueItems,
      totalValue: totalValue,
      avgValue: transfers.length > 0 ? totalValue / transfers.length : 0,
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount: number) => {
    return amount.toFixed(3);
  };

  const getStatusColor = (status: string) => {
    return status === 'Approved' ? 'success' : 'default';
  };

  const getStatusLabel = (status: string) => {
    return status === 'Approved' ? t.approved : t.draft;
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      worksheet.mergeCells('C1:J1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF1565C0' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('C2:J2');
      const addressCell = worksheet.getCell('C2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 10 };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('C3:J3');
      const contactCell = worksheet.getCell('C3');
      contactCell.value = `${t.kwd} | ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9 };
      contactCell.alignment = { horizontal: 'center' };

      worksheet.getRow(4).height = 10;

      worksheet.mergeCells('A5:K5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1565C0' }
      };

      const tabNames = [t.detailedView, t.summaryByFromBranch, t.summaryByToBranch, t.summaryByItem];
      worksheet.mergeCells('A6:K6');
      const tabCell = worksheet.getCell('A6');
      tabCell.value = `📊 ${tabNames[currentTab]}`;
      tabCell.font = { size: 12, bold: true, color: { argb: 'FF1565C0' } };
      tabCell.alignment = { horizontal: 'center' };

      worksheet.getRow(7).height = 10;

      if (currentTab === 0) {
        const data = getFilteredData();
        
        const headers = [
          t.transferNumber, t.transferDate, t.fromBranchName, t.toBranchName,
          t.itemCode, t.itemName, t.unitName,
          t.quantity, t.unitCost, t.totalCost, t.status
        ];
        
        const headerRow = worksheet.getRow(8);
        headers.forEach((header, i) => {
          const cell = headerRow.getCell(i + 1);
          cell.value = header;
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1565C0' }
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        
        let currentRow = 9;
        data.forEach((item, index) => {
          const row = worksheet.getRow(currentRow);
          row.getCell(1).value = item.transferNumber;
          row.getCell(2).value = formatDate(item.transferDate);
          row.getCell(3).value = item.fromBranchName;
          row.getCell(4).value = item.toBranchName;
          row.getCell(5).value = item.itemCode;
          row.getCell(6).value = isRTL ? item.itemNameAr : item.itemName;
          row.getCell(7).value = isRTL ? item.unitNameAr : item.unitName;
          row.getCell(8).value = item.quantity;
          row.getCell(9).value = item.unitCost;
          row.getCell(10).value = item.totalCost;
          row.getCell(11).value = getStatusLabel(item.status);
          
          if (index % 2 === 0) {
            row.eachCell(cell => {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF5F5F5' }
              };
            });
          }
          
          currentRow++;
        });
        
        worksheet.columns = [
          { width: 16 }, { width: 14 }, { width: 20 }, { width: 20 },
          { width: 14 }, { width: 30 }, { width: 12 },
          { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }
        ];
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BranchTransfers_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFillColor(245, 250, 255);
      doc.rect(0, 0, pageWidth, 45, 'F');

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 101, 192);
      doc.text(isRTL ? (companyInfo?.companyNameAr || '') : (companyInfo?.companyName || ''), pageWidth / 2, 15, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(companyInfo?.address || '', pageWidth / 2, 22, { align: 'center' });
      doc.text(`${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`, pageWidth / 2, 27, { align: 'center' });

      doc.setFillColor(21, 101, 192);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(isRTL ? t.titleAr : t.title, pageWidth / 2, 38.5, { align: 'center' });

      doc.setFontSize(9);
      doc.setTextColor(21, 101, 192);
      doc.text(new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB'), pageWidth - 15, 38.5, { align: 'right' });

      const tableHeaders = [
        t.transferDate,
        t.transferNumber,
        t.fromBranchName,
        t.toBranchName,
        t.itemName,
        t.quantity,
        t.totalCost,
      ];

      const tableData: any[] = [];
      const data = getFilteredData();

      data.forEach((item) => {
        tableData.push([
          formatDate(item.transferDate),
          item.transferNumber,
          item.fromBranchName.substring(0, 15),
          item.toBranchName.substring(0, 15),
          (isRTL ? item.itemNameAr : item.itemName).substring(0, 20),
          formatCurrency(item.quantity),
          formatCurrency(item.totalCost),
        ]);
      });

      const stats = getStatistics();
      tableData.push([
        {
          content: 'TOTAL',
          colSpan: 5,
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11,
            halign: 'center'
          }
        },
        {
          content: '',
          styles: { fillColor: [13, 71, 161] }
        },
        {
          content: formatCurrency(stats.totalValue),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        }
      ]);

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 50,
        theme: 'striped',
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 251]
        },
      });

      doc.save(`BranchTransfers_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const stats = getStatistics();

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ 
          background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 'bold'
        }}>
          {t.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.columns}>
            <IconButton onClick={(e) => setColumnsAnchor(e.currentTarget)} color="primary">
              <ColumnsIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<ExcelIcon />} onClick={exportToExcel}
            sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white' }}>
            {t.exportExcel}
          </Button>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={exportToPdf}
            sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white' }}>
            {t.exportPdf}
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
            sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white' }}>
            {t.print}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }} className="no-print">
        <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.transfersCount}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.transfersCount}</Typography>
              </Box>
              <CompareArrows sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.itemsTransferred}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.itemsTransferred}</Typography>
              </Box>
              <Inventory sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalValue}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.totalValue)}</Typography>
              </Box>
              <Assessment sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.avgTransferAmount}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.avgValue)}</Typography>
              </Box>
              <Speed sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>{t.filters}</Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {['today', 'thisWeek', 'thisMonth', 'lastMonth', 'thisYear'].map((preset) => (
            <Chip key={preset} label={t[preset as keyof typeof t]} onClick={() => handleDatePreset(preset)}
              color={datePreset === preset ? 'primary' : 'default'} variant={datePreset === preset ? 'filled' : 'outlined'} />
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))].find(o => o.id === fromBranchFilter) || { id: '' as number | '', label: t.allBranches }}
            onChange={(_, v) => setFromBranchFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.fromBranch} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))].find(o => o.id === toBranchFilter) || { id: '' as number | '', label: t.allBranches }}
            onChange={(_, v) => setToBranchFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.toBranch} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <TextField select fullWidth size="small" label={t.status} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="Draft">{t.draft}</MenuItem>
            <MenuItem value="Approved">{t.approved}</MenuItem>
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateFrom}
              value={dateFromFilter ? new Date(dateFromFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateFromFilter(newValue);
                if (dateToFilter && newValue && dateToFilter < newValue) setDateToFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={dateToFilter ? new Date(dateToFilter) : maxDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateTo}
              value={dateToFilter ? new Date(dateToFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateToFilter(newValue);
                if (dateFromFilter && newValue && dateFromFilter > newValue) setDateFromFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={dateFromFilter ? new Date(dateFromFilter) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>
        </Box>

        <TextField fullWidth size="small" placeholder={t.search} value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setDetailPage(0); }} sx={{ mt: 2 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} />
      </Paper>

      <Paper sx={{ mb: 2 }} className="no-print">
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label={t.detailedView} />
          <Tab label={t.summaryByFromBranch} />
          <Tab label={t.summaryByToBranch} />
          <Tab label={t.summaryByItem} />
        </Tabs>
      </Paper>

      <Box className="print-only">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '2px solid #1565C0' }}>
          {companyInfo?.logoBase64 && (
            <Box><img src={companyInfo.logoBase64} alt="Logo" style={{ height: '40px', maxWidth: '100px' }} /></Box>
          )}
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold', color: '#1565C0', mb: 0.3 }}>
              {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
            </Typography>
            <Typography sx={{ fontSize: '9px', color: '#546E7A' }}>{companyInfo?.address || ''} | 📞 {companyInfo?.phone || ''}</Typography>
          </Box>
          <Box sx={{ width: '100px' }}></Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 1, py: 0.5, background: 'linear-gradient(90deg, #1565C0 0%, #1976D2 100%)', color: 'white' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }}>{isRTL ? t.titleAr : t.title} - {[t.detailedView, t.summaryByFromBranch, t.summaryByToBranch, t.summaryByItem][currentTab]}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontSize: '8px', color: '#546E7A' }}>
          <Typography sx={{ fontSize: '8px' }}>
            {new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}
          </Typography>
          <Typography sx={{ fontSize: '8px' }}>{new Date().toLocaleTimeString(isRTL ? 'ar-KW' : 'en-GB')}</Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          {currentTab === 0 && (
            <>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  {visibleColumns.transferNumber && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.transferNumber}</TableCell>}
                  {visibleColumns.date && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.transferDate}</TableCell>}
                  {visibleColumns.fromBranch && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.fromBranchName}</TableCell>}
                  {visibleColumns.toBranch && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.toBranchName}</TableCell>}
                  {visibleColumns.item && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>}
                  {visibleColumns.quantity && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.quantity}</TableCell>}
                  {visibleColumns.cost && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.unitCost}</TableCell>}
                  {visibleColumns.total && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalCost}</TableCell>}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredData().slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage).map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' }, backgroundColor: index % 2 === 0 ? 'background.default' : 'action.hover' }}>
                    {visibleColumns.transferNumber && <TableCell>{row.transferNumber}</TableCell>}
                    {visibleColumns.date && <TableCell>{formatDate(row.transferDate)}</TableCell>}
                    {visibleColumns.fromBranch && <TableCell>{row.fromBranchName}</TableCell>}
                    {visibleColumns.toBranch && <TableCell>{row.toBranchName}</TableCell>}
                    {visibleColumns.item && (
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                      </TableCell>
                    )}
                    {visibleColumns.quantity && <TableCell align="right">{formatCurrency(row.quantity)}</TableCell>}
                    {visibleColumns.cost && <TableCell align="right">{fmtUnitPrice(row.unitCost)}</TableCell>}
                    {visibleColumns.total && <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.totalCost)}</TableCell>}
                    <TableCell>
                      <Chip label={getStatusLabel(row.status)} size="small" color={getStatusColor(row.status)} />
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredData().length === 0 && (
                  <TableRow><TableCell colSpan={12} align="center" sx={{ py: 5 }}><Typography color="text.secondary">{t.noData}</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={getFilteredData().length}
              page={detailPage}
              onPageChange={(_, p) => setDetailPage(p)}
              rowsPerPage={detailRowsPerPage}
              onRowsPerPageChange={(e) => { setDetailRowsPerPage(parseInt(e.target.value, 10)); setDetailPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="no-print"
            />
            </>
          )}

          {currentTab === 1 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.fromBranchName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.transfersCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByFromBranch().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.branchNameAr : row.branchName}</TableCell>
                    <TableCell align="right">{row.transfersCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(row.totalValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 2 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.toBranchName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.transfersCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByToBranch().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.branchNameAr : row.branchName}</TableCell>
                    <TableCell align="right">{row.transfersCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(row.totalValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 3 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.transfersCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalQuantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.avgTransferAmount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByItem().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                    </TableCell>
                    <TableCell align="right">{row.transfersCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalQuantity)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.avgCost)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(row.totalValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)}>
        {Object.keys(visibleColumns).map((key) => (
          <MenuItem key={key}>
            <FormControlLabel control={<Checkbox checked={visibleColumns[key as keyof typeof visibleColumns]}
              onChange={(e) => setVisibleColumns({ ...visibleColumns, [key]: e.target.checked })} />} label={key} />
          </MenuItem>
        ))}
      </Menu>

      <Box className="print-footer">
        <Typography sx={{ fontSize: '8px', color: '#546E7A' }}>
          {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName} - {isRTL ? t.titleAr : t.title}
          <span style={{ marginLeft: '20px', marginRight: '20px' }}>|</span>
          <span className="page-number"></span>
        </Typography>
      </Box>

      <style>{`
        @media print {
          header, nav, .MuiAppBar-root, .MuiDrawer-root, .MuiToolbar-root { display: none !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 0.5cm 1cm 1.5cm 1cm; size: landscape; }
          * { orphans: 3; widows: 3; }
          table { page-break-after: avoid; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; font-size: 9px; }
          main, .MuiBox-root { margin: 0 !important; padding: 8px !important; max-width: 100% !important; }
          table thead tr { background: linear-gradient(135deg, #1565C0 0%, #1976D2 100%) !important; -webkit-print-color-adjust: exact; }
          table thead th { background: linear-gradient(135deg, #1565C0 0%, #1976D2 100%) !important; color: white !important; font-weight: bold !important; padding: 4px 6px !important; border: 1px solid #1565C0 !important; font-size: 9px !important; -webkit-print-color-adjust: exact; }
          table tbody tr:nth-child(odd) { background-color: #F5F9FF !important; -webkit-print-color-adjust: exact; }
          table tbody tr:nth-child(even) { background-color: #FFFFFF !important; -webkit-print-color-adjust: exact; }
          table tbody td { padding: 3px 6px !important; border: 1px solid #E3F2FD !important; color: #263238 !important; font-size: 8px !important; line-height: 1.2 !important; -webkit-print-color-adjust: exact; }
          table tbody td .MuiTypography-caption { color: #546E7A !important; opacity: 1 !important; }
          .MuiChip-root { height: 16px !important; font-size: 7px !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorSuccess { background-color: #4CAF50 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiTableContainer-root { box-shadow: none !important; border-radius: 0 !important; overflow: visible !important; }
          .print-only h4, .print-only h3, .print-only h5 { color: #1565C0 !important; -webkit-print-color-adjust: exact; }
          .print-only .MuiPaper-root { -webkit-print-color-adjust: exact; box-shadow: none !important; }
          .print-only img { -webkit-print-color-adjust: exact; }
          .print-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 8px; padding: 5px; border-top: 1px solid #1565C0; background: white; }
        }
        @media screen { .print-only { display: none !important; } .print-footer { display: none !important; } }
      `}</style>
    </Box>
  );
};

export default BranchTransfersReport;